import { useState } from 'react';
import { format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { tripsApi, type DatesPreview } from '../../api/trips.api';
import { itineraryApi } from '../../api/itinerary.api';
import { useDateLocale } from '../../utils/dateLocale';
import DateRangeCalendar from '../ui/DateRangeCalendar';
import Modal from '../ui/Modal';
import Spinner from '../ui/Spinner';
import { Button } from '../ui/Button';
import { toast } from '../ui/Toast';
import { Icon, CalendarDays, Plane } from '../ui/Icon';
import type { ItineraryDay, Trip } from '../../types';

/**
 * The trip's dates, changeable after creation.
 *
 * Until this existed the range was fixed at creation: wrong dates, or a flight
 * that moved a day, meant starting the trip over. Changing them re-anchors the
 * itinerary by day number — day N keeps its stops and moves to the new day N —
 * so shifting a trip a week later carries the whole plan with it.
 *
 * A shorter range is the one case that can lose something, so it is never
 * applied blind: the preview says exactly how many stops move and where, and
 * reservations that fall outside are reported rather than silently re-dated.
 */

interface Props {
  trip: Trip;
  canEdit: boolean;
  onUpdated: (trip: Trip) => void;
  /** Refetched after a change: the day rows are what actually moved. */
  onDaysChanged: (days: ItineraryDay[]) => void;
  className?: string;
}

export default function EditableTripDates({ trip, canEdit, onUpdated, onDaysChanged, className = '' }: Props) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();

  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState<{ start: string | null; end: string | null }>({ start: null, end: null });
  const [preview, setPreview] = useState<DatesPreview | null>(null);
  const [busy, setBusy] = useState(false);

  const label = `${format(new Date(trip.startDate), 'd MMM', { locale: dateLocale })} – ${format(new Date(trip.endDate), 'd MMM yyyy', { locale: dateLocale })}`;

  function openPicker() {
    setSel({ start: trip.startDate.slice(0, 10), end: trip.endDate.slice(0, 10) });
    setPreview(null);
    setOpen(true);
  }

  async function askForPreview() {
    if (!sel.start || !sel.end || busy) return;
    setBusy(true);
    try {
      setPreview(await tripsApi.previewDates(trip.id, sel.start, sel.end));
    } catch {
      toast(t('trip.dates.failed'), 'error');
    } finally {
      setBusy(false);
    }
  }

  async function apply() {
    if (!sel.start || !sel.end || busy) return;
    setBusy(true);
    try {
      const updated = await tripsApi.update(trip.id, { startDate: sel.start, endDate: sel.end });
      onUpdated(updated);
      // The days are rows, not something derived from the trip — so they have to
      // be read back, not recomputed here from the new range.
      onDaysChanged(await itineraryApi.listDays(trip.id));
      toast(t('trip.dates.saved'), 'success');
      setOpen(false);
    } catch {
      toast(t('trip.dates.failed'), 'error');
    } finally {
      setBusy(false);
    }
  }

  const outOfRangeCount = preview
    ? preview.outOfRange.flights.length + preview.outOfRange.hotels.length + preview.outOfRange.bookings.length
    : 0;
  const unchanged = sel.start === trip.startDate.slice(0, 10) && sel.end === trip.endDate.slice(0, 10);

  if (!canEdit) return <p className={className}>{label}</p>;

  return (
    <>
      <button
        type="button"
        onClick={openPicker}
        aria-label={t('trip.dates.edit')}
        className={`${className} inline-flex items-center gap-1 hover:underline cursor-pointer`}
      >
        {label}
        <Icon as={CalendarDays} size={10} className="opacity-70" />
      </button>

      <Modal open={open} onClose={() => setOpen(false)} title={t('trip.dates.title')} size="sm">
        <div className="space-y-4">
          <DateRangeCalendar
            start={sel.start}
            end={sel.end}
            onChange={(start, end) => { setSel({ start, end }); setPreview(null); }}
          />

          {preview && (
            <div className="space-y-2.5">
              {preview.plan.movedStops > 0 && (
                <p className="text-sm p-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200">
                  {t('trip.dates.willMove', {
                    count: preview.plan.movedStops,
                    from: preview.plan.dayCount + 1,
                    to: preview.plan.landsOnDayNumber,
                  })}
                </p>
              )}
              {/* ADR-0008: meals go with the day. Saying so here is what keeps
                  this a decision the user made rather than data that vanished. */}
              {preview.plan.removedMeals > 0 && (
                <p className="text-sm p-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200">
                  {t('trip.dates.willLoseMeals', { count: preview.plan.removedMeals })}
                </p>
              )}
              {/* ADR-0017: planned clothes go with the day too. */}
              {preview.plan.removedOutfitDays > 0 && (
                <p className="text-sm p-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200">
                  {t('trip.dates.willLoseOutfits', { count: preview.plan.removedOutfitDays })}
                </p>
              )}
              {preview.plan.addedDays > 0 && (
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {t('trip.dates.willAdd', { count: preview.plan.addedDays })}
                </p>
              )}
              {preview.plan.movedStops === 0 && preview.plan.addedDays === 0 && preview.plan.removedMeals === 0 && preview.plan.removedOutfitDays === 0 && (
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {t('trip.dates.noLoss', { count: preview.plan.dayCount })}
                </p>
              )}

              {outOfRangeCount > 0 && (
                <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 space-y-1.5">
                  <p className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 dark:text-slate-300">
                    <Icon as={Plane} size={12} />
                    {t('trip.dates.bookingsOutside', { count: outOfRangeCount })}
                  </p>
                  <ul className="text-2xs text-slate-500 dark:text-slate-400 space-y-0.5">
                    {preview.outOfRange.flights.map(f => (
                      <li key={f.id}>{f.flightNumber ?? t('trip.dates.aFlight')} · {format(parseISO(f.departureTime), 'd MMM', { locale: dateLocale })}</li>
                    ))}
                    {preview.outOfRange.hotels.map(h => (
                      <li key={h.id}>{h.name} · {format(parseISO(h.checkIn), 'd MMM', { locale: dateLocale })}</li>
                    ))}
                    {preview.outOfRange.bookings.map(b => (
                      <li key={b.id}>{b.title} · {format(parseISO(b.date), 'd MMM', { locale: dateLocale })}</li>
                    ))}
                  </ul>
                  {/* Said plainly: the user is about to wonder, and guessing is
                      worse than a sentence. */}
                  <p className="text-2xs text-slate-400">{t('trip.dates.bookingsKept')}</p>
                </div>
              )}
            </div>
          )}

          <div className="flex gap-2">
            {preview ? (
              <Button variant="cta" className="flex-1" loading={busy} onClick={apply}>
                {t('trip.dates.confirm')}
              </Button>
            ) : (
              <Button
                variant="cta"
                className="flex-1 disabled:opacity-50 disabled:pointer-events-none"
                disabled={!sel.start || !sel.end || unchanged}
                loading={busy}
                onClick={askForPreview}
              >
                {busy ? <Spinner size="xs" /> : t('common.continue')}
              </Button>
            )}
            <button type="button" className="btn-secondary" onClick={() => setOpen(false)}>
              {t('common.cancel')}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
